import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Loader2, Tag, DollarSign } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useUserStore } from '@/client/stores/user';
import {
  useUpsertPriceMutation,
  useUpdatePriceMutation,
  useDeletePriceMutation,
} from '@/client/api/promotion-pricing/promotion-pricing.mutation';
import {
  LIST_PRICE_PRICTID,
  DEFAULT_CCODE,
  DEFAULT_COUNTRY_ISO2,
  CCODE_COUNTRY_ISO2,
} from '@/client/types/promotion-pricing';
import {
  CatalogPlan,
  getPlanListPrice,
  planTitle,
  asNum,
} from '@/client/types/promotion-pricing/catalog';

// Out of scope: multi-currency. Keep the selectable currencies minimal for now —
// the backend supports more, the UI does not yet.
const CURRENCIES = [DEFAULT_CCODE] as const;

const schema = z.object({
  price: z.coerce.number({ invalid_type_error: 'Enter a valid amount' }).positive('Price must be greater than 0'),
  ccode: z.string().min(1, 'Currency is required'),
});

type FormValues = z.infer<typeof schema>;

interface EditPlanPriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: CatalogPlan | null;
  /** Currency the pricing screen is currently scoped to. */
  ccode?: string;
}

const EditPlanPriceModal: React.FC<EditPlanPriceModalProps> = ({
  isOpen,
  onClose,
  plan,
  ccode = DEFAULT_CCODE,
}) => {
  const { mutateAsync: upsertPrice, isPending: isUpserting } = useUpsertPriceMutation();
  const { mutateAsync: updatePrice, isPending: isUpdating } = useUpdatePriceMutation();
  const uidActor = useUserStore((s) => s.user?.uid);
  const { mutateAsync: deletePrice, isPending: isDeleting } = useDeletePriceMutation();

  const isPending = isUpserting || isUpdating || isDeleting;

  const existing = plan ? getPlanListPrice(plan, ccode) : undefined;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { price: existing?.price ?? 0, ccode },
  });

  // Re-seed the form whenever a different plan / currency is opened.
  useEffect(() => {
    if (isOpen) {
      reset({ price: existing?.price ?? 0, ccode: existing?.ccode ?? existing?.currency ?? ccode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, plan?.toid, ccode]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onDelete = async () => {
    if (existing?.pricid == null || !plan) return;
    if (!window.confirm(`Delete this price for "${plan.name}"? A plan with no price cannot be bought.`)) return;
    try {
      await deletePrice({ pricid: existing.pricid });
      toast.success(`Price for "${plan.name}" deleted`, { richColors: true });
      handleClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete price', { richColors: true });
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!plan) return;

    const country_iso2 =
      existing?.country_iso2 ?? CCODE_COUNTRY_ISO2[values.ccode] ?? DEFAULT_COUNTRY_ISO2;

    try {
      // An existing price row carries a `pricid` → update it in place via PUT.
      // Otherwise create it via the idempotent upsert (POST).
      if (existing?.pricid != null) {
        await updatePrice({
          pricid: existing.pricid,
          payload: {
            prictid: LIST_PRICE_PRICTID,
            price: values.price,
            ccode: values.ccode,
            country_iso2,
            ...(uidActor != null ? { uid_actor: uidActor } : {}),
          },
        });
      } else {
        // Creating a price requires the acting admin's uid (backend missing_fields otherwise).
        if (uidActor == null) {
          toast.error('Cannot set price: your admin identity is unavailable. Sign in again.', {
            richColors: true,
          });
          return;
        }
        await upsertPrice({
          prictid: LIST_PRICE_PRICTID,
          toid: plan.toid,
          ccode: values.ccode,
          country_iso2,
          price: values.price,
          uid_actor: uidActor,
        });
      }
      toast.success(`Price for "${plan.name}" saved`, { richColors: true });
      handleClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save price', { richColors: true });
    }
  });

  const watchedPrice = watch('price');
  const watchedCcode = watch('ccode');

  const durationDays = plan ? asNum(plan.sub_duration) : null;
  const durationLabel =
    durationDays != null ? `${durationDays} day${durationDays === 1 ? '' : 's'}` : null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Tag className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-gray-900">Edit Plan Price</DialogTitle>
              <DialogDescription>
                {plan ? planTitle(plan) : 'Update the list price for this promotion plan.'}
                {durationLabel ? ` · ${durationLabel}` : ''}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={onSubmit} className="mt-4 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5 sm:col-span-1">
              <Label htmlFor="ccode">Currency</Label>
              <select
                id="ccode"
                {...register('ccode')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              {errors.ccode && <p className="text-xs text-red-500">{errors.ccode.message}</p>}
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="price">
                Amount <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">
                  {watchedCcode === 'GHS' ? '₵' : ''}
                </span>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className={watchedCcode === 'GHS' ? 'pl-8' : ''}
                  {...register('price')}
                />
              </div>
              {errors.price && <p className="text-xs text-red-500">{errors.price.message}</p>}
            </div>
          </div>

          {existing && (
            <p className="text-xs text-gray-500">
              Current list price:{' '}
              <span className="font-semibold text-gray-700">
                {existing.ccode ?? existing.currency} {Number(existing.price).toLocaleString()}
              </span>
            </p>
          )}

          {/* Preview */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
              <DollarSign className="h-4 w-4" />
              New price
            </div>
            <span className="font-mono text-xl font-bold text-blue-800">
              {watchedCcode === 'GHS' ? '₵' : `${watchedCcode} `}
              {Number(watchedPrice || 0).toLocaleString()}
            </span>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t">
            {existing?.pricid != null && (
              <Button
                type="button"
                variant="outline"
                onClick={onDelete}
                disabled={isPending}
                className="mr-auto border-red-200 text-red-600 hover:bg-red-50"
              >
                Delete price
              </Button>
            )}
            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
              <X className="h-4 w-4 mr-1.5" />
              Cancel
            </Button>
            <Button type="submit" disabled={isPending} className="bg-blue-600 hover:bg-blue-700 text-white">
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Tag className="h-4 w-4 mr-1.5" />
                  Save Price
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditPlanPriceModal;
